/**
 * Cofre do escritório: certificados digitais (A1 com o arquivo guardado, A3 só o registro) e senhas de portais.
 * Segredo NUNCA volta em listagem: arquivo e senha saem só pelas rotas de "baixar" e "ver senha", que ficam no audit_log.
 */
import { z } from 'zod';
import { DataISO, Id } from './comum.js';
import { SituacaoVencimento } from './vencimento.js';

export const TipoCertificado = z.enum(['A1', 'A3']);
export type TipoCertificado = z.infer<typeof TipoCertificado>;

export const Certificado = z.object({
  id: Id, clienteId: Id.nullable(), clienteNome: z.string().nullable(), tipo: TipoCertificado, titular: z.string(),
  documento: z.string().nullable(),
  /** o CNPJ/CPF do certificado não é o do cliente em que foi guardado */
  documentoDiverge: z.boolean(),
  emissor: z.string().nullable(), serial: z.string().nullable(), validoDesde: DataISO.nullable(), validade: DataISO,
  dias: z.number().int(), situacao: SituacaoVencimento, avisarDiasAntes: z.number().int(),
  temArquivo: z.boolean(), arquivoNome: z.string().nullable(), temSenha: z.boolean(), observacao: z.string().nullable(), restrito: z.boolean(),
  /** o membro logado pode baixar o arquivo e ver a senha */
  podeAcessar: z.boolean(),
  enviadoPorApelido: z.string().nullable(), criadoEm: z.string(), ultimoAcessoEm: z.string().nullable(), ultimoAcessoApelido: z.string().nullable(),
});
export type Certificado = z.infer<typeof Certificado>;

/** A3 (token/cartão): não tem arquivo — titular e validade vêm digitados. */
export const CertificadoA3Body = z.object({
  clienteId: Id.nullable().default(null), titular: z.string().trim().min(2).max(200), validade: DataISO,
  observacao: z.string().trim().max(500).nullable().default(null), restrito: z.boolean().default(false), avisarDiasAntes: z.number().int().min(0).max(365).default(30),
});
export type CertificadoA3Body = z.infer<typeof CertificadoA3Body>;
export const CertificadoEditarBody = z.object({
  clienteId: Id.nullable().optional(), observacao: z.string().trim().max(500).nullable().optional(), restrito: z.boolean().optional(), avisarDiasAntes: z.number().int().min(0).max(365).optional(),
  /** só A3 */
  titular: z.string().trim().min(2).max(200).optional(), validade: DataISO.optional(),
});
export type CertificadoEditarBody = z.infer<typeof CertificadoEditarBody>;

export const SenhaGuardadaBody = z.object({
  /** nulo = senha do próprio escritório */
  clienteId: Id.nullable().default(null),
  sistema: z.string().trim().min(2).max(80),
  url: z.string().trim().max(300).nullable().default(null),
  usuario: z.string().trim().max(200).nullable().default(null),
  senha: z.string().min(1).max(500),
  observacao: z.string().trim().max(1000).nullable().default(null),
  restrito: z.boolean().default(false),
  expiraEm: DataISO.nullable().default(null),
});
export type SenhaGuardadaBody = z.infer<typeof SenhaGuardadaBody>;
/** Edição: só o que vier é alterado; sem `senha` a senha guardada fica como está. */
export const SenhaGuardadaEditarBody = z.object({
  clienteId: Id.nullable().optional(), sistema: z.string().trim().min(2).max(80).optional(), url: z.string().trim().max(300).nullable().optional(), usuario: z.string().trim().max(200).nullable().optional(),
  senha: z.string().min(1).max(500).optional(), observacao: z.string().trim().max(1000).nullable().optional(), restrito: z.boolean().optional(), expiraEm: DataISO.nullable().optional(),
});
export type SenhaGuardadaEditarBody = z.infer<typeof SenhaGuardadaEditarBody>;

export const SenhaGuardada = z.object({
  id: Id, clienteId: Id.nullable(), clienteNome: z.string().nullable(), sistema: z.string(), url: z.string().nullable(), usuario: z.string().nullable(), observacao: z.string().nullable(),
  restrito: z.boolean(), podeAcessar: z.boolean(), expiraEm: DataISO.nullable(), diasParaExpirar: z.number().int().nullable(),
  senhaAlteradaEm: z.string(), atualizadoPorApelido: z.string().nullable(), ultimoAcessoEm: z.string().nullable(), ultimoAcessoApelido: z.string().nullable(),
});
export type SenhaGuardada = z.infer<typeof SenhaGuardada>;

export const CofreQuery = z.object({ clienteId: Id.optional(), busca: z.string().trim().max(120).optional() });
export const SegredoRevelado = z.object({ senha: z.string() });
export type SegredoRevelado = z.infer<typeof SegredoRevelado>;
export const CofreAcesso = z.object({ quando: z.string(), quem: z.string(), acao: z.string(), ip: z.string().nullable() });
export type CofreAcesso = z.infer<typeof CofreAcesso>;

/** Portais que mais aparecem num escritório contábil — sugestões do campo "sistema" (aceita qualquer texto). */
export const SISTEMAS_SENHA = [
  'e-CAC (código de acesso)', 'gov.br', 'MTE / Empregador Web', 'eSocial', 'FGTS Digital', 'Conectividade Social (Caixa)', 'Simples Nacional (código de acesso)', 'PGMEI / Portal do Empreendedor',
  'Prefeitura / NFS-e', 'SEFAZ / Posto Fiscal', 'Junta Comercial', 'INSS / Meu INSS', 'DET (Domicílio Eletrônico Trabalhista)', 'Banco', 'Sistema contábil', 'E-mail do cliente', 'Outro',
] as const;

/** Tranca do cofre: com a opção do escritório ligada, o segredo só sai depois do código das duas etapas (vale alguns minutos). */
export const CofreEstado = z.object({ exige2fa: z.boolean(), tem2fa: z.boolean(), abertoAte: z.string().nullable() });
export type CofreEstado = z.infer<typeof CofreEstado>;
export const CofreDesbloquearBody = z.object({ codigo: z.string().trim().min(6).max(20) });
